import React, { useState } from "react";
import { motion } from "framer-motion";
import { Film, Bell, ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/button";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VideoDemoSection({ setShowVideo, navigate }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/contact/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "video_demo_section" }),
      });
    } catch (error) {
      console.error('Newsletter subscribe failed:', error);
    }
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <section id="video-demo" className="relative z-10 px-6 md:px-12 lg:px-24 py-16 bg-[#0A0A0F]">
      {/* Dot pattern */}
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="max-w-4xl mx-auto relative">
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
        >
          {/* Video placeholder card */}
          <div
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-[#39FF14]/5"
            data-testid="video-demo-player"
          >
            <div className="bg-gradient-to-br from-[#0F0F1A] via-[#1A1A2E] to-[#0F0F1A] flex flex-col items-center justify-center relative py-16 md:py-24 px-6">
              <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

              <div className="relative z-10 flex flex-col items-center text-center max-w-lg">
                {/* Coming Soon badge */}
                <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6">
                  <Film className="w-3.5 h-3.5" />
                  Coming Soon
                </div>

                {/* Heading */}
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Demo Video Coming Soon
                </h2>

                {/* Subtitle */}
                <p className="text-gray-400 text-base md:text-lg mb-8 leading-relaxed">
                  We're putting the finishing touches on our product walkthrough. Drop your email to get notified when it's live.
                </p>

                {/* Email form or success */}
                {submitted ? (
                  <div className="flex items-center gap-2 text-[#39FF14] font-medium">
                    <Check className="w-5 h-5" />
                    You're on the list! We'll notify you when it's ready.
                  </div>
                ) : (
                  <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 w-full max-w-md" data-testid="video-notify-form">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-[#39FF14] focus:ring-1 focus:ring-[#39FF14] text-sm"
                      data-testid="video-notify-input"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#39FF14] text-black hover:bg-[#32E012] font-semibold px-6 py-3 h-auto text-sm whitespace-nowrap"
                      data-testid="video-notify-btn"
                    >
                      <Bell className="w-4 h-4 mr-1.5" />
                      {loading ? "Saving..." : "Notify Me"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            No learning curve. No complex setup. <span className="font-medium text-gray-300">Live in minutes.</span>
          </p>

          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-[#39FF14] to-[#00D4FF] text-black hover:opacity-90 font-semibold px-8 py-5 text-base h-auto shadow-lg shadow-[#39FF14]/20"
              data-testid="video-cta-btn"
            >
              Get Founding Member Access <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
